import { useTranslation } from 'react-i18next';
import { Button, Flex, useTabContext } from '@electricjs/arc';
import SelectFilter from 'common/TableFilters/SelectFilter';
import { AssetStatus, ProductTypeName } from '@electricjs/core_entity-client';

import {
  getDeviceFilterOptions,
  getStatusOptions,
  getTypeOptions,
  getWarrantyOptions,
} from '@/pages/Assets/TableFilters/filterOptions';
import {
  ASSET_NOT_ARCHIVED_STATUSES,
  ASSET_UNAVAILABLE_STATUSES,
} from '@/hooks/useGetEmployeesWithAssets';
import {
  AssetManagementTabs,
  IsDeviceFilterOption,
  Warranty,
} from '@/pages/Assets/types';

type AssetsTableFiltersGroupProps = {
  showStatusFilter: boolean;
  showTypeFilter: boolean;
  showWarrantyFilter: boolean;
  showIsDeviceFilter: boolean;
  setAssetStatus: (
    assetStatus: AssetStatus[] | ((prev: AssetStatus[]) => AssetStatus[])
  ) => void;
  assetStatus: AssetStatus[];
  setProductType: (
    productType:
      | ProductTypeName[]
      | ((prev: ProductTypeName[]) => ProductTypeName[])
  ) => void;
  productType: ProductTypeName[];
  setWarranty: (
    warranty: Warranty[] | ((prev: Warranty[]) => Warranty[])
  ) => void;
  warranty: Warranty[];
  isMultiSelect?: boolean;
  setExcludeAssigned: (excludeAssigned: boolean) => void;
  setIsDevice: (
    isDevice:
      | IsDeviceFilterOption[]
      | ((prev: IsDeviceFilterOption[]) => IsDeviceFilterOption[])
  ) => void;
  isDevice: IsDeviceFilterOption[];
};

const AssetsTableFiltersGroup = ({
  showStatusFilter,
  showTypeFilter,
  showWarrantyFilter,
  showIsDeviceFilter,
  setAssetStatus,
  assetStatus,
  setProductType,
  productType,
  setWarranty,
  warranty,
  isMultiSelect = false,
  setExcludeAssigned,
  setIsDevice,
  isDevice,
}: AssetsTableFiltersGroupProps) => {
  const { t } = useTranslation('assets');
  const { selectedId: selectedTab } = useTabContext();

  const statusOptions = getStatusOptions(t);
  const typeOptions = getTypeOptions();
  const warrantyOptions = getWarrantyOptions(t);
  const deviceFilterOptions = getDeviceFilterOptions(t);

  const modifiedStatusOptions = statusOptions.filter(option =>
    selectedTab === AssetManagementTabs.ARCHIVED
      ? ASSET_UNAVAILABLE_STATUSES.has(option.value)
      : ASSET_NOT_ARCHIVED_STATUSES.has(option.value)
  );

  const handleAddStatus = (status: AssetStatus) => {
    if (isMultiSelect) {
      setAssetStatus((prev: AssetStatus[]) => {
        if (prev.includes(status)) {
          return prev.filter(s => s !== status);
        }
        return [...prev, status];
      });
    } else {
      // Single select mode
      setAssetStatus([status]);
    }
  };

  const handleAddType = (type: ProductTypeName) => {
    if (isMultiSelect) {
      setProductType(prev => {
        if (prev.includes(type)) {
          return prev.filter(t => t !== type);
        }
        return [...prev, type];
      });
    } else {
      // Single select mode
      setProductType([type]);
    }
  };

  const handleAddWarranty = (warranty: Warranty) => {
    if (isMultiSelect) {
      setWarranty(prev => {
        if (prev.includes(warranty)) {
          return prev.filter(w => w !== warranty);
        }
        return [...prev, warranty];
      });
    } else {
      // Single select mode
      setWarranty([warranty]);
    }
  };

  const handleAddIsDevice = (newIsDevice: IsDeviceFilterOption) => {
    if (isDevice[0] === newIsDevice) {
      return setIsDevice([]);
    }
    setIsDevice([newIsDevice]);
  };

  // clearing all filters
  const handleClearFilters = () => {
    setAssetStatus([]);
    setProductType([]);
    setWarranty([]);
    setIsDevice([]);
    setExcludeAssigned(false);
  };

  const showClearButton =
    assetStatus.length > 0 ||
    productType.length > 0 ||
    warranty.length > 0 ||
    isDevice.length > 0;

  return (
    <Flex justifyContent="space-between" flexGrow="1">
      <Flex gap="1.6rem" alignItems="center" flexWrap="wrap">
        {showStatusFilter && (
          <SelectFilter
            filterName={t('tableFilters.status')}
            options={modifiedStatusOptions}
            selectedValues={assetStatus}
            handleAddFilter={handleAddStatus}
            showSelectedValuesChips
          />
        )}
        {showTypeFilter && (
          <SelectFilter
            filterName={t('tableFilters.type')}
            options={typeOptions}
            selectedValues={productType}
            handleAddFilter={handleAddType}
            showSelectedValuesChips
          />
        )}
        {showWarrantyFilter && (
          <SelectFilter
            filterName={t('tableFilters.warranty')}
            options={warrantyOptions}
            selectedValues={warranty}
            handleAddFilter={handleAddWarranty}
            showSelectedValuesChips
          />
        )}
        {showIsDeviceFilter && (
          <SelectFilter
            filterName={t('tableFilters.isDevice')}
            options={deviceFilterOptions}
            selectedValues={isDevice}
            handleAddFilter={handleAddIsDevice}
            showSelectedValuesChips
          />
        )}
        <Flex gap="1.6rem" alignItems="center">
          {showClearButton && (
            <Button
              id="clear-all-filters"
              whiteSpace="nowrap"
              variant="text"
              intent="primary"
              size="small"
              onClick={handleClearFilters}>
              {t('tableFilters.clearAll')}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AssetsTableFiltersGroup;
